import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Helmet } from "react-helmet-async";
import React from "react";
import { Link } from "wouter";
import "./RadarGuide.css";

const GUIDE_SECTIONS = [
  { id: "what-it-is", label: "What Radar is, and what it isn't" },
  { id: "tiers", label: "How attention gets allocated" },
  { id: "rank-momentum", label: "Attention becomes measurable" },
  { id: "provenance", label: "Nothing happened vs we didn't look" },
  { id: "lifecycle", label: "Lifecycle" },
  { id: "reading-the-page", label: "Reading the Radar page" },
  { id: "limits", label: "What Radar does not claim" },
] as const;

const TECH_ARTICLE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "TechArticle",
  headline: "How Market Radar Works",
  url: "https://tyl.ee/radar/guide",
  description:
    "Radar is an adaptive market-observation system. It allocates measurement resolution to the markets that earn it, which makes attention itself measurable.",
  about: "Adaptive market observation and attention-based market ranking",
  publisher: {
    "@type": "Organization",
    name: "Tylee Smart Markets",
    url: "https://tyl.ee",
  },
};

type ProgressionStripProps = {
  progression: "tiers" | "lifecycle";
  label: string;
  items: string[];
  descriptions: string[];
  className?: string;
};

function ProgressionStrip({
  progression,
  label,
  items,
  descriptions,
  className,
}: ProgressionStripProps): JSX.Element {
  return (
    <div
      className={["radar-guide-progression", className].filter(Boolean).join(" ")}
      data-progression={progression}
    >
      <ol className="radar-guide-progression-steps" aria-label={label} role="list">
        {items.map((item, index) => (
          <li className="radar-guide-progression-step" key={item}>
            <strong>{item}</strong>
            <span className="radar-guide-progression-description">
              {descriptions[index]}
            </span>
            {index < items.length - 1 ? (
              <span className="radar-guide-progression-connector" aria-hidden="true">
                →
              </span>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

function ProvenancePanel(): JSX.Element {
  return (
    <aside
      className="radar-guide-provenance"
      data-testid="provenance-callout"
      role="note"
    >
      <p className="radar-guide-provenance-heading">
        "Nothing happened" is different from "we didn't look."
      </p>
      <div className="radar-guide-provenance-grid">
        <div className="radar-guide-provenance-case">
          <span className="radar-guide-meta">NOT MEASURED</span>
          <code>null</code>
          <span>Radar did not look.</span>
        </div>
        <div className="radar-guide-provenance-case">
          <span className="radar-guide-meta">MEASURED ZERO</span>
          <code>0</code>
          <span>Radar looked and measured zero.</span>
        </div>
      </div>
    </aside>
  );
}

function ExampleBlock(): JSX.Element {
  return (
    <figure className="radar-guide-example">
      <figcaption>
        <span className="radar-guide-meta">EXACT RANK EXAMPLE</span>
        CURRENT RANK 19 compared with PRICE CHANGE +4.7%
      </figcaption>
      <div className="radar-guide-example-grid">
        <div className="radar-guide-example-value">
          <span>RANK</span>
          <strong>19</strong>
        </div>
        <div className="radar-guide-example-value">
          <span>PREVIOUS RANK</span>
          <strong>83</strong>
        </div>
        <div className="radar-guide-example-value radar-guide-example-value--up">
          <span>RANK MOMENTUM</span>
          <strong>
            <span aria-hidden="true">↑</span> +64
          </strong>
        </div>
        <div className="radar-guide-example-value radar-guide-example-value--price">
          <span>PRICE CHANGE</span>
          <strong>+4.7%</strong>
        </div>
      </div>
    </figure>
  );
}

const RadarGuide: React.FC = () => {
  // JSON-LD injection via useEffect + document.createElement instead of Helmet block.
  // Attempted: <script type="application/ld+json" dangerouslySetInnerHTML={{__html: ...}} />
  // inside Helmet, with test polling via custom waitFor (30 attempts, 10ms intervals).
  // Result: Helmet renders title/meta/link correctly but does not emit script children
  // to document.head in jsdom test environment despite react-helmet-async v2 supporting it.
  // Direct DOM injection via useEffect succeeds reliably. Future maintainers: if jsdom
  // or Helmet behavior changes, try: (1) Helmet.script() if available, (2) Helmet context API,
  // (3) check HelmetProvider configuration in render helper.
  React.useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(TECH_ARTICLE_JSONLD);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>How Market Radar Works | Tylee Smart Markets</title>
        <meta
          name="description"
          content="Radar is an adaptive market-observation system. It allocates measurement resolution to the markets that earn it, which makes attention itself measurable."
        />
        <link rel="canonical" href="https://tyl.ee/radar/guide" />
      </Helmet>

      <div className="radar-guide-wrapper">
        <a href="#radar-guide-main" className="skip-link">Skip to main content</a>
        <AppHeader />

        <main className="radar-guide-main" id="radar-guide-main">
          <div className="radar-guide">
            <div className="radar-guide-intro">
              <span className="radar-guide-context">MARKET RADAR GUIDE</span>
              <h1>How Market Radar Works</h1>
            </div>

            <nav className="radar-guide-index" aria-label="On this page">
              <span className="radar-guide-index-label">ON THIS PAGE</span>
              {GUIDE_SECTIONS.map(({ id, label }) => (
                <a key={id} href={`#${id}`}>{label}</a>
              ))}
            </nav>

            <div className="radar-guide-body">
              <section id="what-it-is" aria-labelledby="what-it-is-heading">
                <h2 id="what-it-is-heading">What Radar is, and what it isn't</h2>
                <p>
                  Radar is not a traditional market screener. It uses adaptive measurement:
                  continuously observing the broad market at low cost while allocating
                  progressively richer real-time instrumentation to markets that earn its
                  attention.
                </p>
                <p>
                  A screener runs the same query across every symbol and sorts the output.
                  Radar does something structurally different. It decides which markets are
                  worth looking at more closely, then changes how closely it looks.
                </p>
              </section>

              <section id="tiers" aria-labelledby="tiers-heading">
                <h2 id="tiers-heading">How attention gets allocated</h2>
                <p>
                  Observation runs in three tiers. At the time of writing Radar is observing
                  roughly 785 markets, with most in SCAN, a smaller WATCH set, and only a
                  handful in DEEP.
                </p>
                <ProgressionStrip
                  progression="tiers"
                  label="Radar measurement tiers"
                  items={["SCAN", "WATCH", "DEEP"]}
                  descriptions={[
                    "REST ticker polling only.",
                    "Ticker plus a live trade WebSocket.",
                    "Ticker, trades and order-book depth.",
                  ]}
                />
                <dl>
                  <dt>SCAN</dt>
                  <dd>
                    REST ticker polling only. The widest and cheapest layer. Trade, aggressor
                    and depth-quality metrics are not measured here.
                  </dd>
                  <dt>WATCH</dt>
                  <dd>
                    Ticker plus a live trade WebSocket. Trade metrics are measured; shallow book
                    depth is still not.
                  </dd>
                  <dt>DEEP</dt>
                  <dd>Ticker, trades and order-book depth. Every metric is measured.</dd>
                </dl>
                <p>
                  Markets are promoted and demoted between tiers as their behavior changes, so
                  measurement resolution follows evidence. A quiet market does not earn
                  order-book instrumentation merely by existing.
                </p>
              </section>

              <section id="rank-momentum" aria-labelledby="rank-momentum-heading">
                <h2 id="rank-momentum-heading">Attention becomes measurable</h2>
                <p>
                  Because Radar allocates its own attention, movement through that attention
                  hierarchy is itself information. A market that climbs from rank 83 to rank 19
                  is telling you something categorically different from a market whose price
                  rose 4.7 percent.
                </p>
                <ExampleBlock />
                <p>
                  Price, volume, volatility, order flow and book imbalance are measurements of
                  the market. Radar rank and rank momentum are measurements of Radar's changing
                  attention to the market. Attention itself becomes measurable.
                </p>
                <p>
                  This is why a market can appear in Radar's movers list having barely moved in
                  price. A symbol falling nine rank positions while its micro-volatility reads
                  zero is not a broken row. It is the system reporting that the structure it was
                  watching has become less interesting. The same is true in reverse: a market
                  climbing sharply in rank can surface developing activity before that activity
                  appears on a conventional price-movers list.
                </p>
              </section>

              <section id="provenance" aria-labelledby="provenance-heading">
                <h2 id="provenance-heading">"Nothing happened" vs "we didn't look"</h2>
                <ProvenancePanel />
                <p>
                  Measurement provenance is part of Radar's data model, not a display detail. A
                  null metric means Radar did not spend the measurement budget needed to know.
                  A zero means Radar looked and measured zero.
                </p>
                <p>
                  A SCAN market with no depth-quality reading is not reporting a shallow order
                  book. It is reporting that nobody subscribed to its order book. A DEEP market
                  reporting zero did subscribe, and measured zero. Without that distinction,
                  adaptive measurement would be ambiguous in a way that quietly corrupts every
                  conclusion drawn from it.
                </p>
              </section>

              <section id="lifecycle" aria-labelledby="lifecycle-heading">
                <h2 id="lifecycle-heading">Lifecycle</h2>
                <p>
                  Alongside tier, each market carries a lifecycle state describing where it sits
                  in an activity cycle:
                </p>
                <ProgressionStrip
                  progression="lifecycle"
                  label="Radar activity lifecycle"
                  items={[
                    "DORMANT",
                    "EMERGING",
                    "ACCELERATING",
                    "PEAK",
                    "ACTIVE",
                    "DECAYING",
                  ]}
                  descriptions={[
                    "— quiet, with no meaningful activity.",
                    "— activity beginning to register.",
                    "— activity building quickly.",
                    "— activity at its local maximum.",
                    "— sustained elevated activity.",
                    "— activity receding.",
                  ]}
                />
                <p>
                  Lifecycle and tier answer different questions. Tier tells you how closely Radar
                  is currently looking. Lifecycle tells you what it is seeing.
                </p>
              </section>

              <section id="reading-the-page" aria-labelledby="reading-the-page-heading">
                <h2 id="reading-the-page-heading">Reading the Radar page</h2>
                <p>
                  On the <Link href="/radar">Radar board</Link>, read the columns in this order:
                </p>
                <ul>
                  <li>
                    <strong>Tier</strong> — how much instrumentation this market currently earns,
                    and therefore which metrics are measured at all.
                  </li>
                  <li>
                    <strong>Lifecycle</strong> — what stage of an activity cycle Radar believes
                    this market is in.
                  </li>
                  <li>
                    <strong>Rank and rank momentum</strong> — where the market sits in Radar's
                    attention ordering, and which direction it is traveling.
                  </li>
                  <li>
                    <strong>Activity and ranking scores</strong> — the composite measures behind
                    that ordering.
                  </li>
                </ul>
                <p>
                  A blank metric is meaningful. Before drawing a conclusion from an empty cell,
                  check the tier: at SCAN, most order-flow metrics are simply not measured.
                </p>
              </section>

              <section id="limits" aria-labelledby="limits-heading">
                <h2 id="limits-heading">What Radar does not claim</h2>
                <p>
                  Radar rank momentum measures change in Radar's attention to a market. It does
                  not measure price momentum, and Tylee Smart Markets does not currently claim
                  that rank momentum predicts future price movement.
                </p>
                <p>
                  What Radar does claim is narrower and testable: rank momentum measures change
                  in market attention rather than change in price, and can therefore surface
                  developing activity before that activity appears on a conventional price-movers
                  list. Whether that pattern reliably relates to subsequent price moves is an
                  open question we have not yet answered statistically.
                </p>
                <p>
                  Radar also does not give trading advice, and nothing here is a recommendation
                  to buy or sell.
                </p>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default RadarGuide;
